import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, destroy, get, post } from '~/lib/api'
import type { User } from '~/lib/types'

export const currentUserQuery = queryOptions({
  queryKey: ['current-user'],
  queryFn: async () => {
    try {
      return await get<User>('/api/v1/me')
    } catch (error) {
      // Signed out is a valid state, not an error the UI should surface.
      if (error instanceof ApiError && error.status === 401) return null
      throw error
    }
  },
})

export function useCurrentUser() {
  return useQuery(currentUserQuery)
}

export function useSignIn() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      post<User>('/api/v1/session', { session: credentials }),
    onSuccess: (user) => client.setQueryData(currentUserQuery.queryKey, user),
  })
}

export function useSignUp() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (attrs: { email: string; password: string; name: string }) =>
      post<User>('/api/v1/registration', { user: attrs }),
    onSuccess: (user) => client.setQueryData(currentUserQuery.queryKey, user),
  })
}

export function useSignOut() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: () => destroy<null>('/api/v1/session'),
    onSuccess: () => {
      client.setQueryData(currentUserQuery.queryKey, null)
      client.removeQueries({ queryKey: ['dashboard'] })
      client.removeQueries({ queryKey: ['users'] })
    },
  })
}
